import { FC } from 'react';

// Quarks
import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Image, { ImageData } from 'quarks/Image';
import Text from 'quarks/Text';
import type { FlexProps } from 'quarks/interpolations/flex';

import richTextParser from 'utils/richTextParser';
import useSwipeable from 'utils/useSwipeable';

// Types
import type { ContentfulComponentTimeline, ContentfulElementTimelineItem } from 'graphqlTypes';

interface TimelineProps extends FlexProps, ContentfulComponentTimeline {
  backgroundIsDark?: boolean;
}

export type TimelineItemProps = FlexProps & ContentfulElementTimelineItem;

const ComponentTimeline: FC<TimelineProps> = ({ panels, ...props }) => {
  const length = panels?.length || 0;
  const [active, setActive, cardContainerRef] = useSwipeable(length);

  return (
    <Flex
      position="relative"
      gap="30px"
      justifyContent="flex-start"
      minWidth={`calc(100% * ${length} + (30px * ${length - 1}))`}
      transform={`translateX(-${active * 50}px)`}
      transition="transform 400ms cubic-bezier(0.215, 0.610, 0.355, 1.000)"
      height="calc(750px)"
      display="none"
      sm={{ display: 'flex', transform: `translateX(-${active * 75}px)` }}
      md={{ transform: `translateX(-${active * 70}px)` }}
      lg={{ transform: `translateX(-${active * 50}px)` }}
      ref={cardContainerRef}
      {...props}
    >
      {panels &&
        panels.map((item, idx) => {
          const itemProp = item as TimelineItemProps;

          return (
            <Flex
              className="tabbed-number"
              key={item?.id}
              borderLeft="2px solid"
              borderColor={active === idx ? 'navy-50' : 'navy-900'}
              width="100%"
              overflowX="hidden"
              overflowY="hidden"
              maxWidth="100%"
              height="100%"
              cursor="pointer"
              backgroundColor="navy-900"
              position="absolute"
              top="0"
              left={`calc(${idx} * 75px)`}
              transition="500ms cubic-bezier(0.215, 0.610, 0.355, 1.000)"
              transform={active >= idx ? 'translateX(0px)' : 'translateX(435px)'}
              sm={{ maxWidth: '500px' }}
              lg={{
                maxWidth: '734px',
                left: `calc(${idx} * 80px)`,
                transform: active >= idx ? 'translateX(0px)' : 'translateX(695px)',
              }}
              onClick={() => setActive(idx)}
            >
              <Flex
                width="90px"
                height="fit-content"
                fontSize="textXs"
                fontWeight="bold"
                textColor={active === idx ? 'common-white' : 'navy-300'}
              >
                <Flex transform="rotate(270deg)" position="relative" left="6px" top="6px">
                  {item?.year}
                </Flex>
              </Flex>
              <Flex
                flexDirection="column"
                width="100%"
                gap="16px"
                transition="500ms ease-in-out"
                opacity={idx === length - 1 && active !== idx ? '0' : `${active === idx ? '1' : '0'}`}
                md={{ gap: '24px' }}
              >
                {itemProp?.featuredImage && (
                  <Container
                    width="100%"
                    borderRadius="16px"
                    overflowX="hidden"
                    overflowY="hidden"
                    md={{ marginBottom: 16 }}
                  >
                    <Image
                      image={itemProp.featuredImage as ImageData}
                      alt={itemProp.featuredImage.title || ''}
                      width="100%"
                    />
                  </Container>
                )}
                {itemProp?.heading && (
                  <Text
                    fontSize="textLg"
                    lineHeight="textLg"
                    fontWeight="bold"
                    textColor="common-white"
                    md={{ fontSize: 'h5', lineHeight: 'h5' }}
                  >
                    {itemProp.heading}
                  </Text>
                )}
                {itemProp?.body && (
                  <Flex
                    width="100%"
                    flexDirection="column"
                    fontSize="textSm"
                    lineHeight="textSm"
                    textColor="gray-50"
                    gap="16px"
                    md={{ fontSize: 'textLg', lineHeight: 'textLg' }}
                  >
                    {richTextParser(itemProp.body)}
                  </Flex>
                )}
              </Flex>
            </Flex>
          );
        })}
    </Flex>
  );
};

export default ComponentTimeline;
